import { useState } from "react";
import { Link } from "wouter";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import Icon from "@/components/mnf/Icon";
import { GlassCard, PillTag, Btn, Avatar } from "@/components/mnf/UI";
import {
  useStoreQuery,
  useStoreMutation,
  queries,
  mutations,
  type NetworkLifeStage,
  type NetworkGoal,
  type NetworkProfile,
} from "@/lib/store";

/**
 * SisterNetwork
 *
 * The Sister Network hub. Three states:
 *
 *  1. Locked — sister has not yet walked 90 consecutive days with the same
 *     accountability sister. Shows a progress card with days completed and a
 *     gentle explanation of why the gate exists.
 *  2. Eligible, not yet opted in — shows an opt-in card that explains the
 *     network, the 5-active-connection cap, and the auto-lapse rule.
 *  3. Member — shows the browse grid, life-stage and goal filter pills, and
 *     a button into the intros inbox.
 *
 * The browse grid is text-first on purpose: bio, life stage, and goal pills.
 * Photos and locations are intentionally omitted so the matching logic stays
 * about season and goal, never appearance.
 */

const LIFESTAGE_PILLS: { key: NetworkLifeStage; label: string }[] = [
  { key: "single",       label: "Single" },
  { key: "married",      label: "Married" },
  { key: "mama",         label: "Mama" },
  { key: "pregnant",     label: "Pregnant" },
  { key: "postpartum",   label: "Postpartum" },
  { key: "empty-nester", label: "Empty-nester" },
];

const GOAL_PILLS: { key: NetworkGoal; label: string }[] = [
  { key: "strength",       label: "Strength" },
  { key: "nourishment",    label: "Nourishment" },
  { key: "scripture",      label: "Scripture" },
  { key: "motherhood",     label: "Motherhood" },
  { key: "marriage",       label: "Marriage" },
  { key: "grief",          label: "Grief" },
  { key: "body-image",     label: "Body image" },
  { key: "rest",           label: "Rest" },
  { key: "perimenopause",  label: "Perimenopause" },
];

const INTRO_PROMPTS = [
  "Hi sister, I noticed we are walking through a similar season. I've been there. May I share what helped me?",
  "Sister, your bio met me right where I am. Could we walk together for a season?",
  "Hello, I'm praying through something close to your goals. Would a short conversation be a gift?",
  "Sister, your testimony is one I needed. May I ask one honest question?",
];

export default function SisterNetwork() {
  const me = useStoreQuery(() => queries.currentUser());
  // No fallback to the seeded demo account: opting in, sending intros, and browsing
  // below all key off this id. See the early return just below.
  const userId = me?.id ?? null;
  const eligible = useStoreQuery(() => (userId ? queries.networkEligible(userId) : false));
  const daysIn = useStoreQuery(() => (userId ? queries.daysWithCurrentSister(userId) : 0));
  const myProfile = useStoreQuery(() => (userId ? queries.myNetworkProfile(userId) : null));
  const isMember = !!myProfile?.isOpenIn;

  if (!userId) {
    return (
      <div className="px-5 pt-6">
        <p className="mnf-body" style={{ color: "var(--mnf-tg)" }}>
          Sign in to see the Sister Network, sister.
        </p>
      </div>
    );
  }

  return (
    <>
      <ScreenHeader
        variant="forest"
        eyebrow="Sister Network"
        title="Walk with veterans"
        subtitle={
          <>
            "Two are better than one, because they have a good return for their labor."
            <span style={{ color: "rgba(229,185,151,0.95)" }}> · Ecclesiastes 4:9</span>
          </>
        }
        trailing={
          isMember ? (
            <Link
              href="/sister-network/intros"
              className="no-underline flex items-center justify-center hover-elevate"
              style={{
                width: 36, height: 36, borderRadius: "50%",
                background: "rgba(245,240,232,0.12)", color: "#F5F0E8",
                border: "1px solid rgba(245,240,232,0.18)",
              }}
              aria-label="Your intros"
              data-testid="link-sister-network-intros"
            >
              <Icon name="forum" size={18} />
            </Link>
          ) : null
        }
      />

      <div className="px-5 pt-4 pb-28 space-y-4" style={{ background: "var(--mnf-cream)" }}>
        {!eligible && <LockedCard daysIn={daysIn} />}
        {eligible && !isMember && <OptInCard userId={userId} />}
        {eligible && isMember && <MemberView userId={userId} myProfile={myProfile!} />}
      </div>
    </>
  );
}

/* ----------------------------- Locked state ----------------------------- */

function LockedCard({ daysIn }: { daysIn: number }) {
  const target = 90;
  const pct = Math.min(100, (daysIn / target) * 100);
  return (
    <GlassCard padding={22} data-testid="card-network-locked">
      <div className="flex items-center justify-between mb-3">
        <PillTag tone="purple">
          <Icon name="lock" size={11} /> Earned with time
        </PillTag>
        <p className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)" }}>
          {daysIn} of {target} days
        </p>
      </div>

      <h2 className="mnf-serif" style={{ fontSize: 22, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.2 }}>
        90 days with one sister, then the network opens
      </h2>
      <p className="mnf-body mt-3" style={{ fontSize: 13.5, color: "var(--mnf-tg)", lineHeight: 1.55 }}>
        The Sister Network is for veterans, sisters who have stayed in covenant with the same accountability sister for a full season of three months. That depth shapes the kind of conversations that happen here.
      </p>

      <div
        style={{
          height: 6, borderRadius: 999,
          background: "rgba(30,74,53,0.08)",
          overflow: "hidden", marginTop: 18,
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${pct}%`,
            background: "linear-gradient(90deg, var(--mnf-f) 0%, var(--mnf-purple) 100%)",
            borderRadius: 999,
            transition: "width 600ms cubic-bezier(0.34, 1.56, 0.64, 1)",
          }}
          data-testid="progress-network-eligibility"
        />
      </div>
      <p className="mnf-eyebrow mt-2" style={{ color: "var(--mnf-tg)" }}>
        {target - daysIn} days to go
      </p>

      <div
        className="flex items-start gap-3 p-3 mt-5"
        style={{
          background: "var(--mnf-fiberBg)",
          border: "1px solid var(--mnf-fiberBorder)",
          borderRadius: 14,
        }}
      >
        <span style={{ fontSize: 20 }}>🌿</span>
        <p className="mnf-body italic" style={{ fontSize: 12, color: "#2C6248", lineHeight: 1.5 }}>
          Your accountability sister stays your primary walking partner. The network never replaces her. It widens the room a little for the sisters who have proven they can stay.
        </p>
      </div>
    </GlassCard>
  );
}

/* ------------------------------ Opt-in card ----------------------------- */

function OptInCard({ userId }: { userId: string }) {
  const [bio, setBio] = useState("");
  const [lifeStage, setLifeStage] = useState<NetworkLifeStage>("married");
  const [goals, setGoals] = useState<NetworkGoal[]>([]);
  const join = useStoreMutation(mutations.joinNetwork);

  const toggleGoal = (g: NetworkGoal) => {
    setGoals((prev) => (prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]));
  };

  return (
    <GlassCard padding={22} data-testid="card-network-optin">
      <PillTag tone="forest">
        <Icon name="check_circle" size={11} filled /> You're eligible
      </PillTag>
      <h2 className="mnf-serif mt-3" style={{ fontSize: 22, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.2 }}>
        Open the door, sister
      </h2>
      <p className="mnf-body mt-3" style={{ fontSize: 13, color: "var(--mnf-tg)", lineHeight: 1.55 }}>
        A short bio and one or two goals are all the network needs. You can step out at any time.
      </p>

      <div className="mt-5">
        <label className="mnf-eyebrow block mb-2" style={{ color: "var(--mnf-tg)" }}>One soft sentence</label>
        <textarea
          data-testid="input-network-bio"
          value={bio}
          onChange={(e) => setBio(e.target.value.slice(0, 160))}
          rows={3}
          placeholder="Mama of three, learning to receive grace at the same pace I lift heavy."
          className="w-full mnf-body outline-none resize-none"
          style={{
            background: "rgba(255,255,255,0.7)",
            border: "1px solid rgba(30,74,53,0.10)",
            borderRadius: 14,
            padding: 12,
            fontSize: 13.5, color: "var(--mnf-fd)", lineHeight: 1.5,
          }}
        />
        <p className="mnf-body mt-1" style={{ fontSize: 10.5, color: "var(--mnf-tg)", textAlign: "right" }}>
          {bio.length} / 160
        </p>
      </div>

      <div className="mt-3">
        <label className="mnf-eyebrow block mb-2" style={{ color: "var(--mnf-tg)" }}>Life stage</label>
        <div className="flex flex-wrap gap-2">
          {LIFESTAGE_PILLS.map((l) => {
            const active = lifeStage === l.key;
            return (
              <button
                key={l.key}
                onClick={() => setLifeStage(l.key)}
                data-testid={`chip-lifestage-${l.key}`}
                className="hover-elevate active-elevate-2"
                style={{
                  padding: "7px 14px",
                  borderRadius: 999,
                  background: active ? "var(--mnf-f)" : "rgba(30,74,53,0.06)",
                  color: active ? "#F5F0E8" : "var(--mnf-f)",
                  border: active ? "1px solid var(--mnf-f)" : "1px solid transparent",
                  fontSize: 12, fontWeight: 600, cursor: "pointer",
                }}
              >
                {l.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-3">
        <label className="mnf-eyebrow block mb-2" style={{ color: "var(--mnf-tg)" }}>Goals (pick up to 3)</label>
        <div className="flex flex-wrap gap-2">
          {GOAL_PILLS.map((g) => {
            const active = goals.includes(g.key);
            return (
              <button
                key={g.key}
                onClick={() => toggleGoal(g.key)}
                data-testid={`chip-goal-${g.key}`}
                disabled={!active && goals.length >= 3}
                className="hover-elevate active-elevate-2"
                style={{
                  padding: "7px 14px",
                  borderRadius: 999,
                  background: active ? "var(--mnf-t)" : "rgba(192,120,74,0.10)",
                  color: active ? "#fff" : "var(--mnf-t)",
                  border: active ? "1px solid var(--mnf-t)" : "1px solid transparent",
                  fontSize: 12, fontWeight: 600, cursor: "pointer",
                  opacity: !active && goals.length >= 3 ? 0.4 : 1,
                }}
              >
                {g.label}
              </button>
            );
          })}
        </div>
      </div>

      <div
        className="flex items-start gap-3 p-3 mt-5"
        style={{
          background: "rgba(127,119,221,0.08)",
          border: "1px solid rgba(127,119,221,0.22)",
          borderRadius: 14,
        }}
      >
        <span style={{ fontSize: 18 }}>🤝</span>
        <p className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-fd)", lineHeight: 1.5 }}>
          Up to 5 active sister connections at a time. Threads quietly close after 14 days of silence so no one feels obligated.
        </p>
      </div>

      <Btn
        variant="primary"
        block
        size="lg"
        onClick={() => bio.trim() && goals.length > 0 && join({ userId, bio: bio.trim(), lifeStage, goals })}
        disabled={!bio.trim() || goals.length === 0}
        className="mt-4"
        data-testid="button-join-network"
      >
        Open the door <Icon name="arrow_forward" size={16} />
      </Btn>
    </GlassCard>
  );
}

/* ------------------------------ Member view ----------------------------- */

function MemberView({ userId, myProfile }: { userId: string; myProfile: NetworkProfile }) {
  const [filterStage, setFilterStage] = useState<NetworkLifeStage | null>(null);
  const [filterGoal, setFilterGoal] = useState<NetworkGoal | null>(null);
  const members = useStoreQuery(() =>
    queries.networkMembers(userId, {
      lifeStage: filterStage ?? undefined,
      goal: filterGoal ?? undefined,
    }),
  );
  const sentCount = useStoreQuery(() => queries.activeSentIntroCount(userId));
  const leave = useStoreMutation(mutations.leaveNetwork);

  return (
    <>
      <GlassCard padding={18} data-testid="card-my-network-profile">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <PillTag tone="forest">
              <Icon name="check_circle" size={11} filled /> Member
            </PillTag>
            <h2 className="mnf-serif mt-2" style={{ fontSize: 18, fontWeight: 500, color: "var(--mnf-fd)" }}>
              Your network presence
            </h2>
          </div>
          <p className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)", textAlign: "right" }}>
            {sentCount} of 5 active
          </p>
        </div>
        <p className="mnf-body italic" style={{ fontSize: 13, color: "var(--mnf-tg)", lineHeight: 1.5 }}>
          "{myProfile.bio}"
        </p>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {myProfile.goals.map((g) => (
            <PillTag key={g} tone="coral">{g.replace("-", " ")}</PillTag>
          ))}
        </div>
        <button
          onClick={() => leave(userId)}
          data-testid="button-leave-network"
          className="mnf-body mt-3"
          style={{
            fontSize: 11.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
            color: "var(--mnf-tg)", background: "transparent", border: "none",
            cursor: "pointer", padding: 0,
          }}
        >
          Step out for now
        </button>
      </GlassCard>

      <p className="mnf-eyebrow" style={{ color: "var(--mnf-tg)" }}>Browse veterans</p>

      <div className="space-y-2">
        <div className="flex flex-wrap gap-2">
          <FilterChip label="All stages" active={!filterStage} onClick={() => setFilterStage(null)} testId="filter-stage-all" />
          {LIFESTAGE_PILLS.map((l) => (
            <FilterChip
              key={l.key}
              label={l.label}
              active={filterStage === l.key}
              onClick={() => setFilterStage(filterStage === l.key ? null : l.key)}
              testId={`filter-stage-${l.key}`}
            />
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <FilterChip label="All goals" active={!filterGoal} onClick={() => setFilterGoal(null)} testId="filter-goal-all" tone="coral" />
          {GOAL_PILLS.map((g) => (
            <FilterChip
              key={g.key}
              label={g.label}
              active={filterGoal === g.key}
              onClick={() => setFilterGoal(filterGoal === g.key ? null : g.key)}
              testId={`filter-goal-${g.key}`}
              tone="coral"
            />
          ))}
        </div>
      </div>

      {members.length === 0 && (
        <GlassCard padding={20}>
          <p className="mnf-body italic" style={{ fontSize: 13, color: "var(--mnf-tg)", textAlign: "center" }}>
            No sisters match those filters yet. Try widening the search.
          </p>
        </GlassCard>
      )}

      {members.map((m) => (
        <NetworkMemberCard key={m.userId} member={m} userId={userId} sentCount={sentCount} />
      ))}
    </>
  );
}

function FilterChip({
  label, active, onClick, testId, tone = "forest",
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  testId: string;
  tone?: "forest" | "coral";
}) {
  const colors = tone === "forest"
    ? { activeBg: "var(--mnf-f)", activeColor: "#F5F0E8", idleBg: "rgba(30,74,53,0.06)", idleColor: "var(--mnf-f)" }
    : { activeBg: "var(--mnf-t)", activeColor: "#fff", idleBg: "rgba(192,120,74,0.10)", idleColor: "var(--mnf-t)" };
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="hover-elevate active-elevate-2"
      style={{
        padding: "6px 12px",
        borderRadius: 999,
        background: active ? colors.activeBg : colors.idleBg,
        color: active ? colors.activeColor : colors.idleColor,
        border: "1px solid transparent",
        fontSize: 11.5, fontWeight: 600, cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

function NetworkMemberCard({
  member, userId, sentCount,
}: {
  member: NetworkProfile;
  userId: string;
  sentCount: number;
}) {
  const author = useStoreQuery(() => queries.userById(member.userId));
  const [introOpen, setIntroOpen] = useState(false);
  const sendIntro = useStoreMutation(mutations.sendIntro);
  const [prompt, setPrompt] = useState(INTRO_PROMPTS[0]);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const onSend = () => {
    setError("");
    const result = sendIntro({
      fromUserId: userId,
      toUserId: member.userId,
      prompt,
      customNote: note,
    });
    if (result && "error" in result) {
      setError(result.error);
    } else {
      setSent(true);
    }
  };

  return (
    <GlassCard padding={16} data-testid={`card-member-${member.userId}`}>
      <div className="flex items-start gap-3 mb-2">
        <Avatar
          initials={author?.initials ?? "S"}
          src={author?.avatarDataUrl ?? undefined}
          size={44}
          ringColor="var(--mnf-purpleL)"
        />
        <div className="flex-1 min-w-0">
          <p className="mnf-serif" style={{ fontSize: 15.5, fontWeight: 500, color: "var(--mnf-fd)" }}>
            {author?.name ?? "A sister"}
          </p>
          <p className="mnf-body italic" style={{ fontSize: 12, color: "var(--mnf-tg)", marginTop: 1 }}>
            {member.lifeStage.replace("-", " ")}
          </p>
        </div>
      </div>
      <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-fd)", lineHeight: 1.5 }}>
        {member.bio}
      </p>
      <div className="flex flex-wrap gap-1.5 mt-3">
        {member.goals.map((g) => (
          <PillTag key={g} tone="purple">{g.replace("-", " ")}</PillTag>
        ))}
      </div>
      <Btn
        variant="secondary"
        size="sm"
        block
        onClick={() => setIntroOpen(true)}
        disabled={sentCount >= 5}
        className="mt-3"
        data-testid={`button-send-intro-${member.userId}`}
      >
        {sentCount >= 5 ? "5 active connections, full" : "Send a structured intro"}
      </Btn>

      {introOpen && (
        <IntroSheet
          recipientName={author?.name ?? "Sister"}
          prompt={prompt}
          setPrompt={setPrompt}
          note={note}
          setNote={setNote}
          onClose={() => { setIntroOpen(false); setSent(false); setError(""); }}
          onSend={onSend}
          sent={sent}
          error={error}
        />
      )}
    </GlassCard>
  );
}

function IntroSheet({
  recipientName, prompt, setPrompt, note, setNote, onClose, onSend, sent, error,
}: {
  recipientName: string;
  prompt: string;
  setPrompt: (s: string) => void;
  note: string;
  setNote: (s: string) => void;
  onClose: () => void;
  onSend: () => void;
  sent: boolean;
  error: string;
}) {
  return (
    <div
      className="fixed inset-0 flex items-end justify-center"
      style={{ background: "rgba(22,56,40,0.55)", zIndex: 60 }}
      onClick={onClose}
      data-testid="intro-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 420,
          background: "var(--mnf-cream)",
          borderRadius: "24px 24px 0 0",
          padding: 22,
          maxHeight: "88vh",
          overflowY: "auto",
        }}
      >
        {sent ? (
          <div className="text-center py-4">
            <span style={{ fontSize: 36 }}>💛</span>
            <h2 className="mnf-serif mt-3" style={{ fontSize: 22, fontWeight: 500, color: "var(--mnf-fd)" }}>
              Sent with grace
            </h2>
            <p className="mnf-body mt-2" style={{ fontSize: 13, color: "var(--mnf-tg)", lineHeight: 1.55 }}>
              {recipientName} can accept, accept once, or quietly pass. You will not be notified either way.
            </p>
            <Btn variant="primary" block size="lg" onClick={onClose} className="mt-5" data-testid="button-close-intro-sent">
              Close
            </Btn>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>Structured intro</p>
                <h2 className="mnf-serif" style={{ fontSize: 22, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.15 }}>
                  Write to {recipientName}
                </h2>
              </div>
              <button
                onClick={onClose}
                data-testid="button-close-intro"
                className="hover-elevate"
                style={{
                  width: 36, height: 36, borderRadius: 999,
                  background: "rgba(30,74,53,0.06)", color: "var(--mnf-f)",
                  border: "none", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <Icon name="close" size={18} />
              </button>
            </div>

            <p className="mnf-eyebrow mb-2" style={{ color: "var(--mnf-tg)" }}>Pick a prompt</p>
            <div className="space-y-2 mb-4">
              {INTRO_PROMPTS.map((p, i) => (
                <button
                  key={i}
                  onClick={() => setPrompt(p)}
                  data-testid={`button-prompt-${i}`}
                  className="w-full text-left hover-elevate active-elevate-2"
                  style={{
                    padding: "10px 12px",
                    borderRadius: 12,
                    background: prompt === p ? "rgba(30,74,53,0.08)" : "rgba(255,255,255,0.7)",
                    color: "var(--mnf-fd)",
                    border: `1px solid ${prompt === p ? "var(--mnf-f)" : "rgba(30,74,53,0.06)"}`,
                    cursor: "pointer",
                    fontSize: 12.5, lineHeight: 1.45,
                  }}
                >
                  "{p}"
                </button>
              ))}
            </div>

            <p className="mnf-eyebrow mb-2" style={{ color: "var(--mnf-tg)" }}>Add a short note (optional)</p>
            <textarea
              data-testid="input-intro-note"
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 240))}
              rows={3}
              placeholder="One or two honest sentences..."
              className="w-full mnf-body outline-none resize-none"
              style={{
                background: "rgba(255,255,255,0.7)",
                border: "1px solid rgba(30,74,53,0.10)",
                borderRadius: 12,
                padding: 12,
                fontSize: 13, color: "var(--mnf-fd)", lineHeight: 1.5,
              }}
            />
            <p className="mnf-body mt-1" style={{ fontSize: 10.5, color: "var(--mnf-tg)", textAlign: "right" }}>
              {note.length} / 240
            </p>

            {error && (
              <p className="mnf-body mt-3" style={{ fontSize: 12, color: "var(--mnf-t)", lineHeight: 1.5 }} data-testid="text-intro-error">
                {error}
              </p>
            )}

            <Btn
              variant="primary"
              block
              size="lg"
              onClick={onSend}
              className="mt-4"
              data-testid="button-submit-intro"
            >
              Send intro <Icon name="send" size={14} />
            </Btn>
          </>
        )}
      </div>
    </div>
  );
}
